import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';

const StatCard = ({ title, value, change, icon, delay = 0 }) => {
  const isPositive = change > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-gray-800 dark:text-white">{value}</h3>
          <div className={`flex items-center mt-2 ${isPositive ? 'text-[#18BA91]' : 'text-red-500'}`}>
            <span className="text-sm font-medium">{isPositive ? '+' : ''}{change}%</span>
            <svg
              className="w-4 h-4 ml-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isPositive ? "M5 10l7-7m0 0l7 7m-7-7v18" : "M19 14l-7 7m0 0l-7-7m7 7V3"}
              />
            </svg>
            <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last month</span>
          </div>
        </div>
        <div className="p-3 rounded-lg bg-[#CE0E2D]/10 dark:bg-[#CE0E2D]/20">{icon}</div>
      </div>
    </motion.div>
  );
};

const Chart = ({ title, subtitle, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
        <div className="flex space-x-2">
          <button className="text-xs bg-gray-100 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 px-3 py-1 rounded-full">Week</button>
          <button className="text-xs bg-[#CE0E2D] text-white px-3 py-1 rounded-full">Month</button>
          <button className="text-xs bg-gray-100 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 px-3 py-1 rounded-full">Year</button>
        </div>
      </div>

      <div className="h-64 flex items-center justify-center">
        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-lg h-40 flex items-center justify-center">
          <p className="text-gray-400 dark:text-gray-500">Placeholder for {title} chart</p>
        </div>
      </div>
    </motion.div>
  );
};

const RecentModel = ({ name, type, status, accuracy, date, delay = 0 }) => {
  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700"
    >
      <td className="py-4 pl-4">
        <div className="flex items-center">
          <div className="w-8 h-8 mr-3 bg-[#CE0E2D]/10 dark:bg-[#CE0E2D]/20 text-[#CE0E2D] rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <span className="font-medium text-gray-800 dark:text-white">{name}</span>
        </div>
      </td>
      <td className="py-4 px-2 text-gray-600 dark:text-gray-300">{type}</td>
      <td className="py-4 px-2">
        <span className={`px-2 py-1 text-xs rounded-full ${status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
          status === 'In Training' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
          }`}>
          {status}
        </span>
      </td>
      <td className="py-4 px-2">
        <div className="flex items-center">
          <span className="mr-2 dark:text-gray-300">{accuracy}%</span>
          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
            <div className={`h-full rounded-full ${accuracy > 85 ? 'bg-green-500' :
              accuracy > 70 ? 'bg-blue-500' :
                'bg-yellow-500'
              }`} style={{ width: `${accuracy}%` }}></div>
          </div>
        </div>
      </td>
      <td className="py-4 px-2 text-gray-500 dark:text-gray-400">{date}</td>
      <td className="py-4 pr-4 text-right">
        <button className="text-[#CE0E2D] dark:text-blue-400 hover:text-[#0052ac] dark:hover:text-blue-300">View</button>
      </td>
    </motion.tr>
  );
};

const DashboardPage = () => {
  const { user } = useAuth0();
  const [activeTab, setActiveTab] = useState('overview');

  // Placeholder data
  const stats = [
    {
      title: "Customer Touchpoints",
      value: "12,849",
      change: 12.5,
      icon: <svg className="w-6 h-6 text-[#CE0E2D]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    },
    {
      title: "Conversion Rate",
      value: "4.2%",
      change: 2.1,
      icon: <svg className="w-6 h-6 text-[#CE0E2D]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    },
    {
      title: "Customer LTV",
      value: "$850",
      change: 8.3,
      icon: <svg className="w-6 h-6 text-[#CE0E2D]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    },
    {
      title: "Campaign ROI",
      value: "128%",
      change: -3.2,
      icon: <svg className="w-6 h-6 text-[#CE0E2D]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    }
  ];

  const recentModels = [
    { name: "Lookalike Model", type: "Customer Acquisition", status: "Active", accuracy: 92, date: "2023-10-28" },
    { name: "Recency Frequency", type: "Marketing Analytics", status: "Active", accuracy: 88, date: "2023-10-25" },
    { name: "Attribution MARKOV", type: "Channel Attribution", status: "In Training", accuracy: 76, date: "2023-10-20" },
    { name: "Multi-Touch Attribution", type: "Campaign Analytics", status: "Active", accuracy: 94, date: "2023-10-15" },
    { name: "Customer Churn Prediction", type: "Retention", status: "Draft", accuracy: 65, date: "2023-10-10" }
  ];

  return (
    <div className="pt-24 pb-16 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto px-4"
      >

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Welcome back, {user?.name?.split(' ')[0] || 'User'}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">Here's what's happening with your analytics today.</p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filter
            </button>
            <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Export
            </button>
            <button className="px-4 py-2 bg-primary2 rounded-lg text-sm text-white hover:bg-primary3 flex items-center">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Model
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Recent Models</h3>
              <button className="text-sm text-[#CE0E2D] dark:text-blue-400 hover:text-[#0052ac] dark:hover:text-blue-300">View All</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left py-3 pl-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Accuracy</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Updated</th>
                  <th className="text-right py-3 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentModels.map((model, index) => (
                  <RecentModel
                    key={index}
                    name={model.name}
                    type={model.type}
                    status={model.status}
                    accuracy={model.accuracy}
                    date={model.date}
                    delay={index * 0.05 + 0.4}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>



        <div className="mt-10">
          <nav className="flex space-x-6 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'overview'
                ? 'text-[#CE0E2D] border-b-2 border-[#CE0E2D]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'campaigns'
                ? 'text-[#CE0E2D] border-b-2 border-[#CE0E2D]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Campaigns
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'customers'
                ? 'text-[#CE0E2D] border-b-2 border-[#CE0E2D]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Customers
            </button>
            <button
              onClick={() => setActiveTab('channels')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'channels'
                ? 'text-[#CE0E2D] border-b-2 border-[#CE0E2D]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Channels
            </button>
          </nav>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              change={stat.change}
              icon={stat.icon}
              delay={index * 0.1}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Chart
            title="Customer Interactions"
            subtitle="Total interactions across all channels"
            delay={0.2}
          />
          <Chart
            title="Revenue Trends"
            subtitle="Monthly revenue performance"
            delay={0.3}
          />
        </div>


      </motion.div>
    </div>
  );
};

export default DashboardPage; 